// What a card body and a composer use to draw with the dashboard's frame,
// without importing the plugin that draws it (`shared-dashboard`):
//
// - `CardAside` and `CardNote` mark what goes in the frame's head (at the
//   right of the title) and its note (the last line); the frame renders them
//   there through portals. Outside a frame (a story without one, a body
//   rendered as it is) they render nothing.
// - `CardEmpty` is a card's "nothing to show" line.
// - `useDashboardParts` gives a composer (a plugin drawing several cards
//   itself, such as the last game's sections) the real `Card` and `Section`
//   inside `<app.Extension name="dashboard.parts">`, and passthroughs
//   outside it.
import * as React from 'react';
import { createPortal } from 'react-dom';
import type { CardMeta, SectionScreen } from './index.ts';

/** Where a frame takes its card's aside and note: elements the frame draws, filled by portals. */
export interface CardSlots {
  aside: HTMLElement | null;
  note: HTMLElement | null;
}

/** Provided by the frame around a card's body; null outside a frame. */
export const CardSlotsContext = React.createContext<CardSlots | null>(null);

const portal = (target: HTMLElement | null | undefined, children: React.ReactNode) => (target ? createPortal(children, target) : null);

/** Shown at the right of the frame's title: a count, a caption, a toggle. */
export const CardAside: React.FC<{ children?: React.ReactNode }> = ({ children }) => portal(React.useContext(CardSlotsContext)?.aside, children);

/** The frame's last line, at the card's bottom edge: a caption under the content. */
export const CardNote: React.FC<{ children?: React.ReactNode }> = ({ children }) => portal(React.useContext(CardSlotsContext)?.note, children);

/** A card's "nothing to show" line, in the frame's muted small role. */
export const CardEmpty: React.FC<{ children?: React.ReactNode }> = ({ children }) => <p className="dash-empty">{children}</p>;

/** The frame, as a composer draws it: the card's metadata, its body, and optionally its aside and note directly. */
export interface DashboardCardProps extends CardMeta {
  aside?: React.ReactNode;
  note?: React.ReactNode;
  children?: React.ReactNode;
}

/** A section, as a composer draws it: `SectionParams` with content in place of slots. */
export interface DashboardSectionProps {
  title?: string;
  screen?: SectionScreen;
  help?: string;
  maxColumns?: number;
  label?: string;
  head?: React.ReactNode;
  /** Content under the cards, within their width. */
  after?: React.ReactNode;
  /** The cards. */
  children?: React.ReactNode;
}

/** The dashboard's parts for a composer. */
export interface DashboardParts {
  Card: React.ComponentType<DashboardCardProps>;
  Section: React.ComponentType<DashboardSectionProps>;
}

/** Outside `dashboard.parts`: the content without any chrome. */
export const passthroughParts: DashboardParts = {
  Card: ({ children }) => <>{children}</>,
  Section: ({ children, after }) => (
    <>
      {children}
      {after}
    </>
  ),
};

/** Provided by the `dashboard.parts` extension around whatever it wraps. */
export const DashboardPartsContext = React.createContext<DashboardParts>(passthroughParts);

/** The real `Card` and `Section` inside `dashboard.parts`, passthroughs outside it. */
export const useDashboardParts = (): DashboardParts => React.useContext(DashboardPartsContext);
